export type MeResponse = {
  user: { id: string; email: string; fullName?: string; role?: string };
  csrfToken: string | null;
  admin: boolean;
  auth?: string;
  device?: DesktopDeviceSession | null;
  staff?: StaffRecord | null;
  staffRole?: string | null;
  access: {
    allowed?: boolean;
    status?: string;
    reason?: string;
    planName?: string | null;
    expiresAt?: string | null;
    productId?: string;
  };
  pricing: {
    planId: string;
    priceUsd: string;
    billing: string;
    checkoutUrl: string;
    plans?: {
      weekly?: { planId: string; priceUsd: string };
      monthly?: { planId: string; priceUsd: string };
      yearly?: { planId: string; priceUsd: string };
    };
  };
};

async function request<T>(path: string, options: RequestInit = {}, csrf?: string | null): Promise<T> {
  const headers: Record<string, string> = {
    accept: "application/json",
    ...(options.headers as Record<string, string>)
  };
  if (options.body && !(options.body instanceof FormData) && !headers["content-type"]) {
    headers["content-type"] = "application/json";
  }
  if (csrf) headers["x-csrf-token"] = csrf;

  const res = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`) as Error & {
      status?: number;
      data?: unknown;
    };
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

export const api = {
  me: () => request<MeResponse>("/crm-autodialer/api/me"),
  searchMegaReserve: (q: string) => request<{ customers: MegaReserveCustomer[] }>(`/crm-autodialer/api/megafed/customers?q=${encodeURIComponent(q)}`),
  getMegaReserveCustomer: (id: number) => request<MegaReserveDetail>(`/crm-autodialer/api/megafed/customers/${id}`),
  addMegaReserveRemark: (id: number, text: string, csrf: string) => request<{ remark: MegaReserveRemark }>(`/crm-autodialer/api/megafed/customers/${id}/remarks`, { method: "POST", body: JSON.stringify({ text }) }, csrf),
  getSettings: () => request<{ settings: Record<string, unknown> }>("/crm-autodialer/api/settings"),
  saveSettings: (settings: Record<string, unknown>, csrf: string) =>
    request<{ settings: Record<string, unknown> }>(
      "/crm-autodialer/api/settings",
      { method: "PUT", body: JSON.stringify({ settings }) },
      csrf
    ),
  testProvider: (provider: string, csrf: string) =>
    request<{ ok: boolean }>("/crm-autodialer/api/settings/test", {
      method: "POST",
      body: JSON.stringify({ provider })
    }, csrf),
  listContacts: (q = "") =>
    request<{ total: number; items: Contact[] }>(
      `/crm-autodialer/api/contacts?q=${encodeURIComponent(q)}&limit=500`
    ),
  saveContact: (contact: Partial<Contact>, csrf: string) =>
    request<{ contact: Contact }>(
      contact.id ? `/crm-autodialer/api/contacts/${contact.id}` : "/crm-autodialer/api/contacts",
      {
        method: contact.id ? "PUT" : "POST",
        body: JSON.stringify(contact)
      },
      csrf
    ),
  deleteContact: (id: string, csrf: string) =>
    request<{ ok: boolean }>(`/crm-autodialer/api/contacts/${id}`, { method: "DELETE" }, csrf),
  importContacts: (contacts: Partial<Contact>[], listId: string | null, csrf: string) =>
    request<{ imported: number; skipped: number }>(
      "/crm-autodialer/api/contacts/import",
      { method: "POST", body: JSON.stringify({ contacts, listId }) },
      csrf
    ),
  listLists: () => request<{ lists: DialList[] }>("/crm-autodialer/api/lists"),
  saveList: (list: Partial<DialList>, csrf: string) =>
    request<{ list: DialList }>(
      "/crm-autodialer/api/lists",
      { method: "POST", body: JSON.stringify(list) },
      csrf
    ),
  listCampaigns: () =>
    request<{ campaigns: Campaign[]; runtimes: Record<string, RuntimeSnap> }>(
      "/crm-autodialer/api/campaigns"
    ),
  createCampaign: (body: Partial<Campaign>, csrf: string) =>
    request<{ campaign: Campaign }>(
      "/crm-autodialer/api/campaigns",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  getCampaign: (id: string) =>
    request<{ campaign: Campaign; runtime: RuntimeSnap | null; calls: CallLog[] }>(
      `/crm-autodialer/api/campaigns/${id}`
    ),
  campaignAction: (id: string, action: "start" | "pause" | "stop", csrf: string) =>
    request<{ campaign: Campaign; runtime: RuntimeSnap | null }>(
      `/crm-autodialer/api/campaigns/${id}/${action}`,
      { method: "POST", body: "{}" },
      csrf
    ),
  singleDial: (
    body: { phone: string; messageScript?: string; callerId?: string; contactId?: string; numberId?: string },
    csrf: string
  ) =>
    request<{ call: CallLog }>(
      "/crm-autodialer/api/dial",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  hangupCall: (id: string, csrf: string) =>
    request<{ call: CallLog }>(
      `/crm-autodialer/api/calls/${encodeURIComponent(id)}/hangup`,
      { method: "POST", body: "{}" },
      csrf
    ),
  listCalls: (campaignId?: string) =>
    request<{ calls: CallLog[] }>(
      `/crm-autodialer/api/calls${campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : ""}`
    ),
  deleteCall: (id: string, csrf: string) =>
    request<{ ok: boolean; deleted: number }>(`/crm-autodialer/api/calls/${encodeURIComponent(id)}`, { method: "DELETE" }, csrf),
  deleteCalls: (ids: string[], csrf: string) =>
    request<{ ok: boolean; deleted: number }>("/crm-autodialer/api/calls/delete", { method: "POST", body: JSON.stringify({ ids }) }, csrf),
  adminOverview: () => request<AdminOverview>("/crm-autodialer/api/admin/overview"),
  listStaff: () => request<{ staff: StaffRecord[] }>("/crm-autodialer/api/staff"),
  listNumbers: () => request<{ numbers: NumberRecord[] }>("/crm-autodialer/api/numbers"),
  getCampaignRouting: () => request<{ numbers: AgentNumber[]; staff: StaffRecord }>("/crm-autodialer/api/agent/campaign-routing"),
  listTransfers: () => request<{ transfers: Record<string, unknown>[] }>("/crm-autodialer/api/admin/transfers"),
  adminRouting: () => request<{ campaigns: Record<string, unknown>[]; transfers: Record<string, unknown>[]; failures: Record<string, unknown>[] }>("/crm-autodialer/api/admin/routing"),
  listHoldMusic: (service?: string) =>
    request<{ tracks: HoldMusicTrack[] }>(
      `/crm-autodialer/api/hold-music${service ? `?service=${encodeURIComponent(service)}` : ""}`
    ),
  generateHoldMusic: (body: { serviceContext: string; prompt: string; durationMs: number; instrumental: boolean }, csrf: string) =>
    request<{ ok: boolean; track: HoldMusicTrack }>("/crm-autodialer/api/hold-music/generate", {
      method: "POST",
      body: JSON.stringify(body)
    }, csrf),
  uploadHoldMusic: (body: FormData, csrf: string) =>
    request<{ ok: boolean; track: HoldMusicTrack }>("/crm-autodialer/api/hold-music/upload", {
      method: "POST",
      body
    }, csrf),
  activateHoldMusic: (service: string, id: string, csrf: string) =>
    request<{ ok: boolean; track: HoldMusicTrack }>(
      `/crm-autodialer/api/hold-music/${encodeURIComponent(service)}/${encodeURIComponent(id)}/activate`,
      { method: "POST", body: "{}" },
      csrf
    ),
  disableHoldMusic: (service: string, csrf: string) =>
    request<{ ok: boolean; disabled: number }>(
      `/crm-autodialer/api/hold-music/${encodeURIComponent(service)}/disable`,
      { method: "POST", body: "{}" },
      csrf
    ),
  createDesktopSession: (body: { deviceId: string; deviceName: string }, csrf: string) =>
    request<DesktopSessionResponse>(
      "/crm-autodialer/api/desktop/session",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  getAgentConfig: () => request<{ ok: boolean; config: AgentConfig }>("/crm-autodialer/api/agent/config"),
  createStaff: (body: Record<string, unknown>, csrf: string) =>
    request<{ user: Record<string, unknown>; staff: Record<string, unknown> }>(
      "/crm-autodialer/api/staff",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  updateStaff: (userId: string, body: Record<string, unknown>, csrf: string) =>
    request<{ staff: StaffRecord }>(
      `/crm-autodialer/api/staff/${encodeURIComponent(userId)}`,
      { method: "PUT", body: JSON.stringify(body) },
      csrf
    ),
  deleteStaff: (userId: string, csrf: string) =>
    request<{ ok: boolean; staff: StaffRecord }>(
      `/crm-autodialer/api/staff/${encodeURIComponent(userId)}`,
      { method: "DELETE", body: "{}" },
      csrf
    ),
  revokeStaff: (userId: string, csrf: string) =>
    request<{ ok: boolean; staff: StaffRecord; busy?: boolean }>(
      `/crm-autodialer/api/staff/${encodeURIComponent(userId)}/revoke`,
      { method: "POST", body: "{}" },
      csrf
    ),
  restoreStaff: (userId: string, csrf: string) =>
    request<{ ok: boolean; staff: StaffRecord }>(
      `/crm-autodialer/api/staff/${encodeURIComponent(userId)}/restore`,
      { method: "POST", body: "{}" },
      csrf
    ),
  setStaffServiceAccess: (userId: string, serviceContext: string, active: boolean, csrf: string) =>
    request<{ ok: boolean; staff: StaffRecord; busy: boolean; serviceContext: string; active: boolean }>(
      `/crm-autodialer/api/staff/${encodeURIComponent(userId)}/service-access`,
      { method: "POST", body: JSON.stringify({ serviceContext, active }) },
      csrf
    ),
  elevenVoices: () => request<{ voices: Array<{ voice_id: string; name: string }> }>("/crm-autodialer/api/elevenlabs/voices"),
  listDnc: () => request<{ dnc: DncEntry[] }>("/crm-autodialer/api/dnc"),
  addDnc: (body: { phone: string; scope?: string; reason?: string }, csrf: string) =>
    request<{ dnc: DncEntry }>("/crm-autodialer/api/dnc", { method: "POST", body: JSON.stringify(body) }, csrf),
  removeDnc: (id: string, csrf: string) =>
    request<{ ok: boolean }>(`/crm-autodialer/api/dnc/${id}`, { method: "DELETE" }, csrf),
  health: () => request<{ ok: boolean; pbx?: Record<string, unknown> }>("/crm-autodialer/api/health"),
  telephonyDashboard: () => request<TelephonyDashboard>("/crm-autodialer/api/admin/telephony"),
  searchTwilioNumbers: (body: Record<string, unknown>, csrf: string) =>
    request<{ purchased: boolean; items: Array<Record<string, unknown>> }>(
      "/crm-autodialer/api/admin/telephony/numbers/search",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  provisionTwilioNumber: (body: Record<string, unknown>, csrf: string) =>
    request<{ number: NumberRecord; trunkAttached?: boolean }>(
      "/crm-autodialer/api/admin/telephony/numbers/provision",
      { method: "POST", body: JSON.stringify(body) },
      csrf
    ),
  syncTwilioNumbers: (csrf: string) =>
    request<{ imported: number; numbers: NumberRecord[] }>(
      "/crm-autodialer/api/admin/telephony/numbers/sync",
      { method: "POST", body: "{}" },
      csrf
    ),
  assignNumber: (numberId: string, agentUserId: string, csrf: string) =>
    request<{ number: NumberRecord; staff: StaffRecord }>(
      `/crm-autodialer/api/admin/telephony/numbers/${encodeURIComponent(numberId)}/assign`,
      { method: "POST", body: JSON.stringify({ agentUserId }) },
      csrf
    ),
  unassignNumber: (numberId: string, csrf: string) =>
    request<{ number: NumberRecord }>(
      `/crm-autodialer/api/admin/telephony/numbers/${encodeURIComponent(numberId)}/unassign`,
      { method: "POST", body: "{}" },
      csrf
    ),
  suspendNumber: (numberId: string, csrf: string) =>
    request<{ number: NumberRecord }>(
      `/crm-autodialer/api/admin/telephony/numbers/${encodeURIComponent(numberId)}/suspend`,
      { method: "POST", body: "{}" },
      csrf
    ),
  releaseNumber: (numberId: string, confirm: boolean, csrf: string) =>
    request<{ number: NumberRecord }>(
      `/crm-autodialer/api/admin/telephony/numbers/${encodeURIComponent(numberId)}/release`,
      { method: "POST", body: JSON.stringify({ confirm }) },
      csrf
    ),
  sipConfig: (id: string, reveal: boolean, csrf: string) =>
    request<{ config: SipConfigView }>(
      `/crm-autodialer/api/admin/telephony/sip/${encodeURIComponent(id)}/config`,
      { method: "POST", body: JSON.stringify({ reveal }) },
      csrf
    ),
  resetSipPassword: (id: string, confirm: boolean, csrf: string) =>
    request<{ config: SipConfigView; warning?: string }>(
      `/crm-autodialer/api/admin/telephony/sip/${encodeURIComponent(id)}/reset`,
      { method: "POST", body: JSON.stringify({ confirm }) },
      csrf
    ),
  telephonyAudit: () => request<{ audit: Record<string, unknown>[] }>("/crm-autodialer/api/admin/telephony/audit"),
  agentTelephony: () =>
    request<{
      staff: StaffRecord;
      numbers: AgentNumber[];
      sip: SipConfigView;
      routing: { defaultOutboundNumberId?: string | null; selectedReceivingMethod?: string };
    }>("/crm-autodialer/api/agent/telephony"),
  saveForwardingNumber: (phone: string, csrf: string) =>
    request<{ staff: StaffRecord }>(
      "/crm-autodialer/api/agent/telephony/forwarding",
      { method: "PUT", body: JSON.stringify({ phone }) },
      csrf
    ),
  sendForwardingCode: (csrf: string) =>
    request<{ status: string; toMasked?: string }>(
      "/crm-autodialer/api/agent/telephony/forwarding/send-code",
      { method: "POST", body: "{}" },
      csrf
    ),
  verifyForwardingCode: (code: string, csrf: string) =>
    request<{ staff: StaffRecord }>(
      "/crm-autodialer/api/agent/telephony/forwarding/verify",
      { method: "POST", body: JSON.stringify({ code }) },
      csrf
    ),
  saveAgentRouting: (body: { defaultOutboundNumberId?: string; selectedReceivingMethod?: string }, csrf: string) =>
    request<{ staff: StaffRecord }>(
      "/crm-autodialer/api/agent/telephony/routing",
      { method: "PUT", body: JSON.stringify(body) },
      csrf
    ),
  agentSipConfig: (csrf: string, reveal = false) =>
    reveal
      ? request<{ config: SipConfigView }>(
          "/crm-autodialer/api/agent/telephony/sip-config",
          { method: "POST", body: "{}" },
          csrf
        )
      : request<{ config: SipConfigView }>("/crm-autodialer/api/agent/telephony/sip-config")
};

export type MegaReserveCustomer = { id:number; name:string; email:string; phoneMasked:string; accountLast4:string; accountType:string; accountStatus:string; verificationStatus:string; checkingBalance:number; savingsBalance:number };
export type MegaReserveTransaction = { id:number; transaction_id?:string; amount:number; transaction_type:string; status:string; description?:string; initiated_at?:string; completed_at?:string };
export type MegaReserveRemark = { id:string; customerUserId:string; actorUserId:string; actorEmail?:string; text:string; createdAt:string };
export type MegaReserveDetail = { customer:MegaReserveCustomer; transactions:MegaReserveTransaction[]; remarks:MegaReserveRemark[] };

export type DesktopDeviceSession = {
  id: string;
  userId: string;
  deviceId: string;
  deviceName?: string;
  expiresAt?: string;
  lastSeenAt?: string;
  accessAllowed?: boolean;
  admin?: boolean;
};

export type AgentPresence = {
  id?: string;
  userId: string;
  agentId: string;
  deviceId: string;
  deviceSessionId: string;
  role?: string;
  serviceContexts?: string[];
  sipExtension?: string;
  sipEndpoint?: string;
  connectionStatus?: string;
  agentStatus?: string;
  currentCallId?: string | null;
  lastHeartbeatAt?: string | null;
  updatedAt?: string | null;
};

export type AgentConfig = {
  agent_id: string;
  user_id: string;
  display_name?: string;
  email?: string;
  role?: string;
  organization?: string;
  desktop_route_id?: string;
  websocket_url?: string;
  service_permissions?: string[];
  service_contexts?: string[];
  agent_status?: string;
  staff?: StaffRecord;
  sip?: {
    username?: string;
    extension?: string;
    endpoint?: string;
    domain?: string;
    server?: string;
    transport?: string;
    password?: string;
  };
  outbound_numbers?: Array<Record<string, unknown>>;
};

export type DesktopSessionResponse = {
  token: string;
  expiresAt: string;
  user: MeResponse["user"];
  admin: boolean;
  staff?: StaffRecord | null;
  agent?: {
    agent_id: string;
    display_name?: string;
    organization?: string;
    role?: string;
    service_permissions?: string[];
    agent_status?: string;
  } | null;
  config?: AgentConfig | null;
  device: DesktopDeviceSession;
  pbx?: Record<string, unknown>;
};

export type DncEntry = {
  id: string;
  phone: string;
  scope: string;
  reason?: string;
  createdAt: string;
};

export type StaffRecord = {
  userId: string;
  email: string;
  role: string;
  serviceContexts: string[];
  outboundNumberIds?: string[];
  desktopAccess?: boolean;
  sipExtension?: string;
  sipEndpoint?: string;
  forwardingNumber?: string;
  forwardingNumberMasked?: string;
  forwardingNumberVerified?: boolean;
  forwardingEnabled?: boolean;
  forwardingConfirmationRequired?: boolean;
  defaultOutboundNumberId?: string | null;
  selectedReceivingMethod?: "SIP" | "PERSONAL_PHONE";
  accessRevokedAt?: string | null;
  accessRestoredAt?: string | null;
  connectionStatus?: string;
  agentStatus?: string;
  currentCallId?: string | null;
  lastHeartbeatAt?: string | null;
};

export type AgentNumber = { id: string; phone?: string; e164?: string; displayName?: string; label?: string; outboundEnabled?: boolean };

export type HoldMusicTrack = {
  id: string;
  serviceContext: string;
  source: string;
  prompt?: string;
  durationMs?: number | null;
  instrumental?: boolean;
  contentType?: string;
  bytes?: number;
  active?: boolean;
  previewUrl?: string;
  activeUrl?: string | null;
  asteriskSound?: string;
  createdAt?: string | null;
  activatedAt?: string | null;
  disabledAt?: string | null;
};

export type NumberRecord = {
  id: string;
  e164: string;
  enabled: boolean;
  serviceContexts: string[];
  label?: string;
  directions?: string[];
  usages?: string[];
  assignedAgentId?: string | null;
  lifecycleStatus?: string;
  providerNumberSid?: string | null;
  friendlyName?: string;
  capabilities?: { voice?: boolean; sms?: boolean };
  locality?: string;
  region?: string;
  isoCountry?: string;
  monthlyPrice?: string | null;
  addressRequirements?: string;
};

export type SipAccountRecord = {
  id: string;
  agentUserId?: string | null;
  agentEmail?: string;
  sipUsername: string;
  sipDomain?: string;
  sipPort?: number;
  sipTransport?: string;
  accountStatus?: string;
  registrationStatus?: string;
  origin?: string;
  assignedNumberId?: string | null;
};

export type SipConfigView = {
  agentUserId?: string;
  agentEmail?: string;
  assignedNumber?: string | null;
  sipUsername?: string;
  sipDomain?: string;
  sipPort?: number;
  sipTransport?: string;
  sipPassword?: string;
  passwordRevealed?: boolean;
  copyFullConfig?: string | null;
  accountStatus?: string;
};

export type TelephonyDashboard = {
  totalNumbers: number;
  assignedNumbers: number;
  unassignedNumbers: number;
  activeSipAccounts: number;
  registeredSipDevices: number;
  unregisteredSipDevices: number;
  verifiedForwardingNumbers: number;
  failedRoutes: number;
  activeTransfers: number;
  numbers: NumberRecord[];
  sipAccounts: SipAccountRecord[];
  staff: StaffRecord[];
  transfers: Record<string, unknown>[];
  audit: Record<string, unknown>[];
};

export type Contact = {
  id: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  notes?: string;
  listIds?: string[];
  status?: string;
};

export type DialList = {
  id: string;
  name: string;
  description?: string;
};

export type Campaign = {
  id: string;
  name: string;
  listId?: string | null;
  contactIds?: string[];
  messageScript: string;
  ttsProvider: string;
  voiceId?: string;
  provider: string;
  serviceContext?: string;
  maxConcurrent: number;
  callerId?: string;
  ownerAgentId?: string;
  selectedOutboundNumberId?: string | null;
  selectedReceivingMethod?: "SIP" | "PERSONAL_PHONE";
  routingSnapshot?: Record<string, unknown> | null;
  dispatchedAt?: string | null;
  recordingEnabled?: boolean;
  recordingConsent?: boolean;
  status: string;
  stats: Record<string, number>;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
};

export type CallLog = {
  id: string;
  campaignId?: string | null;
  phone: string;
  status: string;
  provider: string;
  messageDelivered?: boolean;
  durationSec?: number;
  error?: string | null;
  createdAt: string;
  mode?: string;
  state?: string;
  hangupReason?: string | null;
};

export type RuntimeSnap = {
  campaignId: string;
  activeCalls: number;
  queued: number;
  maxConcurrent: number;
  provider: string;
};

export type AdminOverview = {
  usersWithSettings: number;
  contacts: number;
  campaigns: number;
  activeCampaigns: number;
  callLogs: number;
  recentCalls: CallLog[];
  activeRuntimes: Array<{
    campaignId: string;
    userId: string;
    active: number;
    queued: number;
    maxConcurrent: number;
    provider: string;
  }>;
  config: {
    maxConcurrentCap: number;
    maxConcurrentDefault: number;
    webhookBase: string;
  };
};
